import { useNavigate } from "react-router-dom";

import { useT } from "@repo/transifex";
import {
  useCreateOrderKiosk,
  useCreateOrderKioskV4,
  useFreedomPayOrder
} from "~/api/orders/hooks/use-create-order";
import { toast } from "~/components/hooks/use-toast";
import { useAppContext } from "~/providers/app";
import { logger } from "~/services/logger";
import { useKioskStore } from "~/routes/__app.kiosk/route";
import { kioskActions } from "~/stores/kiosk-session/actions";
import { kioskSessionStore } from "~/stores/kiosk-session/store";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { useUserProductsKiosk } from "~/api/products/hooks/use-user-products";
import { findUnlistedBasketChoices } from "~/utils/basket-choices/find-unlisted-basket-choices";
import { getUnmetOrderMinimums } from "~/utils/order-minimum/get-unmet-order-minimums";
import { orderMinimumBlockActions } from "~/stores/kiosk-session/order-minimum-block";

import { type KioskPaymentMethod } from "./get-kiosk-payment-buttons";
import { buildFreedomPayRequest } from "./build-freedompay-request";

/**
 * Starts a kiosk payment for the current basket (the command half of
 * checkout) and navigates to the matching payment overlay route.
 *
 * FreedomPay tills run the POI + order creation flow and navigate to
 * `/kiosk/basket/freedompay`; all other tills create the order through
 * `useCreateOrderKiosk` and navigate to `/kiosk/basket/payment`. Both render
 * as full-screen modals over the basket, and because all checkout navigation
 * lives here the flow can be started from either the basket or the menu.
 *
 * This hook deliberately exposes no status — reading the in-flight payment is
 * the job of `useKioskCheckoutStatus`, which is cache-backed and therefore
 * consistent regardless of where `startPayment` was called.
 */
export const useKioskCheckoutStart = () => {
  const navigate = useNavigate();

  const {
    hardware: { freedompayStatus }
  } = useAppContext();
  const till = useKioskStore((state) => state.till);
  const user = useKioskStore((state) => state.user);

  const {
    productsBasket,
    unitTotalPayableInclVat,
    unitTotalPriceExclVat,
    unitTotalDepositExclVat
  } = useKioskProducts();

  const { data: userProducts } = useUserProductsKiosk();

  const t = useT();

  const { mutate: createOrder } = useCreateOrderKiosk();

  const { mutate: startFreedompayPayment } = useFreedomPayOrder();

  const { mutateAsync: createOrderV4 } = useCreateOrderKioskV4();

  return (paymentMethod: KioskPaymentMethod) => {
    const orderId = kioskSessionStore.getState()?.orderId;
    if (!orderId) {
      throw new Error("Order ID is not available");
    }

    const unmetMinimums = getUnmetOrderMinimums({
      basket: kioskSessionStore.getState()?.basket ?? [],
      products: userProducts?.products ?? []
    });
    if (unmetMinimums.length > 0) {
      orderMinimumBlockActions.block(unmetMinimums);
      return;
    }

    // Charging, declaring and showing nothing for such a choice is right, but
    // the sale then differs from what the guest picked. Reported here, once
    // per order: the basket is re-priced on every change in many components.
    const unlistedChoices = findUnlistedBasketChoices({
      basket: kioskSessionStore.getState()?.basket ?? [],
      products: userProducts?.products ?? []
    });
    if (unlistedChoices.length > 0) {
      logger.warn("Kiosk basket has choices the catalog does not list", {
        order_id: orderId,
        till_id: till?.id,
        choices: unlistedChoices
      });
    }

    if (
      paymentMethod === "card" &&
      till?.paymentGatewayConfig?.provider === "freedompay"
    ) {
      if (freedompayStatus === "disconnected") {
        logger.warn(
          "Kiosk card payment blocked: terminal reported disconnected",
          {
            till_id: till?.id,
            freedompay_status: freedompayStatus,
            event: "kiosk_freedompay_gate_blocked"
          }
        );
        toast({
          title: t("Payment device is not connected"),
          description: t("Please connect the payment device to continue"),
          variant: "destructive"
        });
        return;
      }

      const serviceId = kioskActions.session.generateServiceId();

      startFreedompayPayment({
        orderId,
        paymentId: serviceId,
        ...buildFreedomPayRequest({
          productsBasket,
          unitTotalPayableInclVat,
          unitTotalPriceExclVat,
          unitTotalDepositExclVat
        }),
        createOrderAsync: () => {
          return createOrderV4({
            paymentMethod: "card",
            paidWithSaldoUnit: 0,
            paymentId: serviceId
          });
        }
      });
      navigate({
        pathname: "/kiosk/basket/freedompay"
      });
      return;
    }

    createOrder({
      serviceId: kioskActions.session.generateServiceId(),
      paymentMethod,
      paidWithSaldoUnit: 0,
      userId: user?.id,
      childId: user?.childId
    });
    navigate({
      pathname: "/kiosk/basket/payment"
    });
  };
};
